"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.OrganizationClient = void 0;
const base_client_1 = require("@deliverr/base-client");
const commons_logs_1 = require("@deliverr/commons-logs");
const _ = require("lodash");
const SellerServiceUtil_1 = require("./SellerServiceUtil");
const defaultConfig = {
    baseUrl: process.env.SELLER_SERVICE_URL,
    user: process.env.SELLER_BASIC_AUTH_USER,
    password: process.env.SELLER_BASIC_AUTH_PASSWORD,
};
class OrganizationClient extends base_client_1.ApiBasicAuthClient {
    constructor(config = defaultConfig) {
        super({
            baseURL: `${config.baseUrl}/v1`,
            basicAuthPassword: config.password,
            basicAuthUser: config.user,
        });
        if (!config.user || !config.password) {
            commons_logs_1.log.warn("SELLER_BASIC_AUTH_USER and SELLER_BASIC_AUTH_PASSWORD environment variable is not set");
        }
    }
    async createSeller(email, createdBy, phoneNumber) {
        try {
            return await this.post("/organization", {
                email,
                createdBy,
                phoneNumber,
            });
        }
        catch (error) {
            (0, SellerServiceUtil_1.handleSellerServiceError)("createSeller", error);
        }
    }
    async addNewUserToExistingSellerGroup(organizationId, firstName, lastName, email, entities, role, contactAreas = [], phoneNumber) {
        try {
            return await this.post(`/organization/${organizationId}/user`, {
                firstName,
                lastName,
                email,
                entities,
                role,
                contactAreas,
                phoneNumber,
            });
        }
        catch (error) {
            (0, SellerServiceUtil_1.handleSellerServiceError)("addNewUserToExistingSellerGroup", error);
        }
    }
    async getSeller(sellerId, includeAllMembers) {
        try {
            return await this.get("/organization", {
                organizationId: sellerId,
                includeUsers: includeAllMembers ? includeAllMembers : true,
            });
        }
        catch (error) {
            (0, SellerServiceUtil_1.handleSellerServiceError)("getSeller", error);
        }
    }
    async validateSellers(sellerIds) {
        try {
            return await this.post("/organization/validateOrganizationIds", {
                organizationIds: sellerIds,
            });
        }
        catch (error) {
            (0, SellerServiceUtil_1.handleSellerServiceError)("validateSellers", error);
        }
    }
    async updateSellerClaims(sellerId, isCatalogImported, onboardingStage, dealStage, hasChargebeeAccount, showChecklist) {
        const bodyObject = _.pickBy({
            dealStage,
            hasChargebeeAccount,
            isCatalogImported,
            onboardingStage,
            showChecklist,
        }, (value) => typeof value !== "undefined");
        try {
            return await this.put(`organization/${sellerId}/claims/update`, bodyObject);
        }
        catch (error) {
            (0, SellerServiceUtil_1.handleSellerServiceError)("updateSellerClaims", error);
        }
    }
    async assignSellerIdentityToAdminAccount(sellerId) {
        try {
            return await this.put(`/organization/${sellerId}/ghost`, null);
        }
        catch (error) {
            (0, SellerServiceUtil_1.handleSellerServiceError)("assignSellerIdentityToAdminAccount", error);
        }
    }
    async changeUserRoleInOrganization(organizationId, email, isUpgraded) {
        try {
            return await this.put(`/organization/${organizationId}/user/${email}/`, null, { upgrade: isUpgraded });
        }
        catch (error) {
            (0, SellerServiceUtil_1.handleSellerServiceError)("changeUserRoleInOrganization", error);
        }
    }
    async getOrganizationActiveStatus(organizationId) {
        try {
            return await this.get(`/organization/${organizationId}/status`);
        }
        catch (err) {
            (0, SellerServiceUtil_1.handleSellerServiceError)("getOrganizationActiveStatus", err);
        }
    }
    async updateOrganization(organizationId, attributes) {
        try {
            return await this.put(`/organization/${organizationId}`, attributes);
        }
        catch (err) {
            (0, SellerServiceUtil_1.handleSellerServiceError)("updateOrganizationActiveStatus", err);
        }
    }
    async updateOrganizationActiveStatus(organizationId, newStatus) {
        try {
            return await this.put(`/organization/${organizationId}/status/${newStatus}`, null);
        }
        catch (err) {
            (0, SellerServiceUtil_1.handleSellerServiceError)("updateOrganizationActiveStatus", err);
        }
    }
    async resendSignUpEmailInvitation(organizationId, email) {
        try {
            return await this.post(`/organization/${organizationId}/user/${email}/resend_email_invite`, null);
        }
        catch (error) {
            (0, SellerServiceUtil_1.handleSellerServiceError)("resendSignUpEmailInvitation", error);
        }
    }
    async sendEmailNotification(organizationId, sender, templateId, substitutions, subject) {
        try {
            return await this.post(`/organization/${organizationId}/send_email_notification`, {
                substitutions,
                templateId,
                sender,
                subject,
            });
        }
        catch (error) {
            (0, SellerServiceUtil_1.handleSellerServiceError)("resendSignUpEmailInvitation", error);
        }
    }
    async createTestCognitoRecord() {
        try {
            return await this.post("/admin/create-cognito-test-record", null);
        }
        catch (err) {
            (0, SellerServiceUtil_1.handleSellerServiceError)("createTestCognitoRecord", err);
            throw err;
        }
    }
    async addOrganizationAddress(organizationId, address, addressTypes) {
        try {
            return await this.post(`/organization/${organizationId}/address`, {
                address,
                addressTypes,
            });
        }
        catch (error) {
            (0, SellerServiceUtil_1.handleSellerServiceError)("addOrganizationAddress", error);
        }
    }
    async getOrganizationAddress(organizationId) {
        try {
            return await this.get(`/organization/${organizationId}/address`);
        }
        catch (error) {
            (0, SellerServiceUtil_1.handleSellerServiceError)("getOrganizationAddress", error);
        }
    }
    async deleteOrganizationAddress(addressId, organizationId) {
        try {
            return await this.delete(`/organization/${organizationId}/address/${addressId}`);
        }
        catch (error) {
            (0, SellerServiceUtil_1.handleSellerServiceError)("deleteOrganizationAddress", error);
        }
    }
    async isOrganizationDeliverrLite(organizationId) {
        try {
            return await this.getCached(`/organization/${organizationId}/isDeliverrLite`);
        }
        catch (err) {
            (0, SellerServiceUtil_1.handleSellerServiceError)("isOrganizationDeliverrLite", err);
        }
    }
    async validateTestAccounts(sellerIds) {
        if (sellerIds.length === 0) {
            return [];
        }
        try {
            const body = { organizationIds: sellerIds };
            const { data } = await this.post(`/organization/validateTestAccounts`, body);
            return data;
        }
        catch (error) {
            (0, SellerServiceUtil_1.handleSellerServiceError)("validateTestAcounts", error, {
                sellerIds: sellerIds.toString(),
            });
        }
    }
    async setDeliverrLite(organizationId, isDeliverrLite) {
        try {
            return await this.put(`/organization/${organizationId}/setDeliverrLite`, {
                isDeliverrLite,
            });
        }
        catch (err) {
            (0, SellerServiceUtil_1.handleSellerServiceError)("setDeliverrLite", err);
        }
    }
    async getAlgoliaSearchKey(organizationId) {
        try {
            return await this.get(`/organization/${organizationId}/algoliaSearchKey`);
        }
        catch (err) {
            (0, SellerServiceUtil_1.handleSellerServiceError)("getAlgoliaSearchKey", err);
        }
    }
    async getBillingDetails(organizationId) {
        try {
            return await this.get(`/organization/${organizationId}/billingDetails`);
        }
        catch (err) {
            (0, SellerServiceUtil_1.handleSellerServiceError)("getBillingDetails", err);
        }
    }
}
exports.OrganizationClient = OrganizationClient;
//# sourceMappingURL=OrganizationClient.js.map